import axios from 'axios';
import { getToken, onMessage, isSupported } from 'firebase/messaging';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging } from 'firebase/messaging';
import { OptimalDeviceId } from './DeviceId';

const firebaseConfig = {
  apiKey: "AIzaSyA4m-CCaWQ6xt_4hFg5yRCvXCtpZrZoGeA",
  authDomain: "live-chat-waba.firebaseapp.com",
  projectId: "live-chat-waba",
  storageBucket: "live-chat-waba.firebasestorage.app",
  messagingSenderId: "888116568172",
  appId: "1:888116568172:web:fa3b16f4e73a5b1268f849",
  measurementId: "G-RL53SVD6N5"
};

class FirebaseService {
  constructor() {
    this.deviceIdService = new OptimalDeviceId();
    this.messaging = null;
    this.isSupported = false;
    this.app = null;
    this.initPromise = this.initialize();
  }

  async initialize() {
    try {
      // Check if messaging is supported first
      const supported = await isSupported();
      if (!supported) {
        console.warn('Firebase messaging is not supported on this browser/device');
        this.isSupported = false;
        return false;
      }

      // Initialize Firebase app only if it hasn't been initialized
      this.app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

      // Initialize messaging
      this.messaging = getMessaging(this.app);
      this.isSupported = true;

      console.log('Firebase messaging initialized successfully');
      return true;
    } catch (error) {
      console.warn('Error initializing Firebase:', error);
      this.isSupported = false;
      this.messaging = null;
      return false;
    }
  }

  async ensureInitialized() {
    await this.initPromise;
    return this.isSupported;
  }

  async requestNotificationPermission() {
    try {
      if (!('Notification' in window)) {
        console.warn('Notifications not supported');
        return false;
      }

      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  async getFCMToken() {
    try {
      // Wait for initialization and check if supported
      const initialized = await this.ensureInitialized();
      if (!initialized || !this.messaging) {
        console.warn('Firebase messaging not available');
        return null;
      }

      const token = await getToken(this.messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY
      });

      return token;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null; // Return null instead of throwing
    }
  }

  async saveTokenToBackend(token, userId, authToken, api) {
    if (!token) return null;

    const deviceId = await this.deviceIdService.getDeviceId();
    const deviceInfo = this.deviceIdService.getFullDeviceInfo();

    try {
      const response = await axios.post(
        `${api}save-fcm-token`,
        {
          fcm_token: token,
          user_id: userId,
          device_id: deviceId,
          device_type: 'web',
          browser_name: deviceInfo.browser_name,
          browser_version: deviceInfo.browser_version,
          os_name: deviceInfo.os_name,
          device_info: JSON.stringify({
            screen: deviceInfo.screen,
            language: deviceInfo.language,
            timezone: deviceInfo.timezone,
            hardware: {
              concurrency: deviceInfo.hardware_concurrency,
              memory: deviceInfo.device_memory
            }
          })
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          }
        }
      );

      if (response.status === 200) {
        localStorage.setItem('fcm_device_id', deviceId);
        localStorage.setItem('fcm_token', token);
      }

      return response.data;
    } catch (error) {
      console.error('Error saving token to backend:', error);
      // Still save locally even if server fails
      localStorage.setItem('fcm_token', token);
      return null; // Don't throw, just return null
    }
  }

  async setupNotifications(userId, authToken, api) {
    try {
      // Wait for initialization
      const initialized = await this.ensureInitialized();
      if (!initialized) {
        console.warn('Firebase messaging not supported, skipping notification setup');
        return null; // Return null instead of throwing
      }

      // Request permission first
      const hasPermission = await this.requestNotificationPermission();
      if (!hasPermission) {
        console.warn('Notification permission denied');
        return null; // Return null instead of throwing
      }

      // Get FCM token
      const token = await this.getFCMToken();
      if (!token) {
        console.warn('Failed to get FCM token');
        return null; // Return null instead of throwing
      }

      // Save to backend
      await this.saveTokenToBackend(token, userId, authToken, api);

      return token;
    } catch (error) {
      console.error('Error setting up notifications:', error);
      return null; // Return null instead of throwing to prevent app crashes
    }
  }

  setupForegroundMessageListener(callback) {
    // Return empty function if not supported
    if (!this.isSupported || !this.messaging) {
      console.warn('Cannot setup foreground listener - messaging not available');
      return () => { }; // Return empty unsubscribe function
    }

    try {
      const unsubscribe = onMessage(this.messaging, (payload) => {
        console.log('Message received in foreground:', payload);

        if (callback) {
          callback(payload);
        }

        // Show notification only if permission is granted
        if (Notification.permission === 'granted') {
          new Notification(payload.notification.title, {
            body: payload.notification.body,
            icon: payload.notification.icon || '/firebase-logo.png'
          });
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up foreground message listener:', error);
      return () => { }; // Return empty unsubscribe function
    }
  }

  // Helper method to check if messaging is supported
  isMessagingSupported() {
    return this.isSupported;
  }
}

// Export as singleton instance
export default new FirebaseService();